import { Box, Flex, VStack } from '@chakra-ui/react';
import { Select } from '@gamma/ui/form-fields';
import ArrowDownwardSharpIcon from '@gamma/ui/icons/ArrowDownwardSharpIcon';
import ArrowUpwardSharpIcon from '@gamma/ui/icons/ArrowUpwardSharpIcon';
import { ReactNode, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { i18n } from '../../../../shared/localization';

const { labels } = i18n.pages.detections;
const { sort: sortLabel } = labels;

interface SortOption {
  value: string;
  label: ReactNode;
}

const sortOptions: SortOption[] = [
  {
    label: (
      <Flex alignItems="center" data-testid="sort-timestamp-desc">
        <ArrowDownwardSharpIcon mr={2} />
        <Box>{sortLabel.timeDesc}</Box>
      </Flex>
    ),
    value: 'latest_start_timestamp.desc',
  },
  {
    label: (
      <Flex alignItems="center" data-testid="sort-timestamp-asc">
        <ArrowUpwardSharpIcon mr={2} />
        <Box>{sortLabel.timeAsc}</Box>
      </Flex>
    ),
    value: 'latest_start_timestamp.asc',
  },
  {
    label: (
      <Flex alignItems="center" data-testid="sort-severity-desc">
        <ArrowDownwardSharpIcon mr={2} />
        <Box>{sortLabel.severityDesc}</Box>
      </Flex>
    ),
    value: 'rank.severity.desc',
  },
  {
    label: (
      <Flex alignItems="center" data-testid="sort-severity-asc">
        <ArrowUpwardSharpIcon mr={2} />
        <Box>{sortLabel.severityAsc}</Box>
      </Flex>
    ),
    value: 'rank.severity.asc',
  },
];

interface DetectionsSortFormData {
  sortBy: string;
}

interface DetectionsSortParams {
  sortByParam: string;
  activeView: string | null;
  params: URLSearchParams;
  setDetectionsParam: (k: string, v: string) => void;
}

export const DetectionsSort = ({
  activeView,
  sortByParam,
  params,
  setDetectionsParam,
}: DetectionsSortParams) => {
  const defaultFilterValues = {
    sortBy: sortByParam,
  };

  const { control, setValue, watch } = useForm<DetectionsSortFormData>({
    mode: 'onSubmit',
    defaultValues: defaultFilterValues,
  });

  const formData = watch();

  const getSelectedSortValue = (value: string) => {
    return sortOptions.filter((option) => option.value === value);
  };

  useEffect(() => {
    const sortBy = formData?.sortBy;
    if (sortBy) {
      setDetectionsParam('sort_by', sortBy);
    } else {
      params.delete('sort_by');
    }
  }, [formData, params, setDetectionsParam]);

  useEffect(() => {
    setValue('sortBy', sortByParam || defaultFilterValues?.sortBy);
  }, [sortByParam]);

  if (activeView !== 'list') return null;

  return (
    <VStack as="form" spacing={3} alignItems="start">
      <Box w="100%">
        <Controller
          name={'sortBy'}
          control={control}
          render={({ field: { name, value } }) => {
            return (
              <Select
                size="sm"
                name={name}
                label="Sort"
                isMulti={false}
                isLabelHidden={true}
                options={sortOptions}
                data-testid="detection-sort"
                value={getSelectedSortValue(value)}
                onChange={(value) => value && setValue('sortBy', value.value)}
              />
            );
          }}
        />
      </Box>
    </VStack>
  );
};
